<ng-container *vsLet="data$ | async as data">
  <div *ngIf="data?.canManageGatewayInstances" class="mt-4 flex flex-col gap-3">
    <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div class="flex flex-col gap-1">
        <h2 class="text-sm font-semibold text-gray-900">Payment gateway instances</h2>
        <p class="text-sm text-gray-700">
          The default gateway is used for new sales when no other routing rule applies. Changing it does not affect
          payments already assigned to a gateway.
        </p>
      </div>
      <button
        vs-button
        type="button"
        theme="primary"
        class="shrink-0"
        [disabled]="!!newSalesBlockedReason"
        (click)="openCreateDialog()"
      >
        <vs-icon name="plus" slot="start"></vs-icon>
        Add gateway
      </button>
    </div>

    <p *ngIf="newSalesBlockedReason" class="text-sm text-gray-700">{{ newSalesBlockedReason }}</p>

    <div class="rounded-lg border border-warn-300 bg-warn-50 p-3 text-sm text-gray-700">
      <span class="font-semibold text-gray-900">Runoff gateways are for existing payments only.</span>
      They remain available for assigned installments, retries, and refunds, but cannot become the default for new
      sales.
    </div>

    <div *ngIf="data.loading" class="flex items-center justify-center gap-2 rounded-xl border border-gray-200 py-10">
      <vs-icon name="refresh-cw-02" class="animate-spin"></vs-icon>
      <span class="text-sm text-gray-600">Loading payment gateway instances...</span>
    </div>

    <div *ngIf="!data.loading && data.error" class="rounded-xl border border-danger-200 bg-danger-50 p-4">
      <p class="text-sm font-semibold text-gray-900">Gateway instances could not be loaded</p>
      <p class="mt-1 text-sm text-gray-700">{{ data.error }}</p>
      <button vs-button type="button" appearance="outline" class="mt-3" (click)="retryLoad()">Try again</button>
    </div>

    <div
      *ngIf="!data.loading && !data.error && !data.gatewayInstances.length"
      class="flex flex-col items-center gap-2 rounded-xl border border-dashed border-gray-300 px-6 py-10 text-center"
    >
      <vs-icon name="bank-note-01" class="text-2xl text-gray-500"></vs-icon>
      <p class="text-sm font-semibold text-gray-900">No gateway instances yet</p>
      <p class="text-sm text-gray-700">
        Add and validate a payment gateway before it can be used to process new sales.
      </p>
      <button
        vs-button
        type="button"
        theme="primary"
        class="mt-1"
        [disabled]="!!newSalesBlockedReason"
        (click)="openCreateDialog()"
      >
        Add gateway
      </button>
    </div>

    <div
      *ngIf="!data.loading && !data.error && data.gatewayInstances.length"
      class="overflow-hidden rounded-xl border border-gray-200 shadow-sm"
    >
      <vs-table [columns]="(columns$ | async) ?? []" [data]="data.gatewayInstances"></vs-table>
    </div>
    <ng-container *ngFor="let gateway of data.gatewayInstances">
      <p *ngIf="gateway.platformConnectionEnabled === false" class="text-sm text-gray-700">
        <span class="font-semibold">{{ gateway.name }}:</span>
        disabled for new sales. Contact a VectorSolv administrator to enable this connection. Existing assigned payments
        can continue on Active or Runoff gateways.
      </p>
    </ng-container>
    <ws-gateway-routing-rules
      *ngIf="!data.loading && !data.error"
      [gatewayInstances]="data.gatewayInstances"
      [canManage]="data.canManageGatewayInstances === true"
    ></ws-gateway-routing-rules>
  </div>
</ng-container>

<ws-gateway-instance-dialog
  #gatewayDialog
  [canManage]="canManageGatewayInstances"
  (saved)="onGatewaySaved()"
></ws-gateway-instance-dialog>

<vs-dialog
  #lifecycleDialog
  [config]="{
    dialogContainerClass: 'w-[350px] sm:w-[500px] max-w-[600px]',
    disableBackdropDismiss: submittingLifecycleChange,
  }"
>
  <vs-dialog-header
    [config]="{
      mode: 'simple',
      hideCloseButton: submittingLifecycleChange,
      icon: lifecycleDialogIcon,
      iconTheme: lifecycleDialogTheme,
      title: lifecycleDialogTitle,
      subtitle: lifecycleDialogDescription,
      showDivider: true,
    }"
  ></vs-dialog-header>

  <div *ngIf="selectedGateway" class="pt-6">
    <p class="text-sm text-gray-700">
      Gateway: <span class="font-semibold text-gray-900">{{ selectedGateway.name }}</span>
    </p>
  </div>

  <div slot="buttons" class="flex flex-col-reverse sm:flex-row sm:justify-end items-center gap-3 pt-6">
    <button
      vs-button
      class="w-full sm:w-auto"
      appearance="outline"
      [disabled]="submittingLifecycleChange"
      (click)="closeLifecycleConfirmation()"
    >
      Cancel
    </button>
    <button
      vs-button
      class="w-full sm:w-auto"
      [theme]="lifecycleDialogTheme"
      [disabled]="
        !canManageGatewayInstances ||
        submittingLifecycleChange ||
        (lifecycleAction === 'activate' && !!newSalesBlockedReason)
      "
      (click)="confirmLifecycleChange()"
    >
      {{ lifecycleConfirmText }}
      <vs-icon *ngIf="submittingLifecycleChange" name="refresh-cw-02" class="animate-spin"></vs-icon>
    </button>
  </div>
</vs-dialog>
